/**
 * Completa o "Aumentar/Diminuir fonte" do widget Sienna.
 *
 * O DEFEITO
 * ---------
 * Lido do bundle (`sienna-accessibility.umd.js`), o recurso de tamanho de fonte é isto:
 *
 * ```js
 * const SELETORES = 'h1,h2,h3,h4,h5,h6,p,a,dl,dt,li,ol,th,td,span,blockquote,.asw-text';
 *
 * function aplicar(fator = 1) {
 *   document.querySelectorAll(SELETORES).forEach((el) => {
 *     if (ehIcone(el)) return;
 *     const original = Number(el.dataset.aswOrgFontSize) || parseInt(getComputedStyle(el).fontSize);
 *     el.dataset.aswOrgFontSize ||= String(original);
 *     el.style.fontSize = `${original * fator}px`;
 *   });
 * }
 * ```
 *
 * TRÊS consequências, e juntas explicam todos os sintomas relatados:
 *
 * 1. **A lista de seletores é FECHADA e não tem controle de formulário.** Faltam `label`,
 *    `button`, `input`, `select`, `textarea`, `option`, `legend`, `caption`, `summary`,
 *    `small`, `strong`, `b`, `em` e `dd`. Uma tela de cadastro ou um modal — que é quase só
 *    rótulo, campo e botão — praticamente não reage. Trocar a FAMÍLIA da fonte funciona em tudo
 *    porque aquele recurso é injetado como CSS em `html *`; o tamanho é o único aplicado
 *    elemento a elemento.
 *
 * 2. **É aplicado UMA VEZ, sobre o DOM daquele instante.** Tudo que nasce depois — modal
 *    aberto em seguida, linha de grade carregada, aba trocada — nasce sem o ajuste. É por isso
 *    que "alguns" modais funcionam: os que já estavam montados quando o usuário clicou.
 *
 * 3. **O estilo é inline SEM prioridade, e o tema tem utilitárias COM prioridade.** Num
 *    `<p class="font-14">` o widget grava `style="font-size: 5.6px"` e a regra
 *    `.font-14 { font-size: 14px !important }` continua vencendo: o atributo aparece no DOM e a
 *    tela não muda nada. Era o caso do "Código INEP" da Home, e valia para toda `.font-*`.
 *
 * O QUE ESTE MÓDULO FAZ
 * ---------------------
 * Aplica a MESMA conta do widget, com a mesma marca `data-asw-org-font-size` e as mesmas
 * exceções de ícone, nos três pontos que faltavam: sobre os elementos que a lista dele não
 * alcança, sobre o que entra no DOM depois, e COM prioridade, para vencer as utilitárias do
 * tema. Idempotente por construção — a escala sai sempre do tamanho ORIGINAL guardado na marca,
 * nunca do tamanho já escalado, então reaplicar mil vezes dá o mesmo resultado.
 *
 * Nada do widget é substituído ou desligado: quem manda no fator continua sendo ele, e o valor
 * é lido do mesmo lugar onde ele o persiste (`asw-user-settings`).
 */

/** Onde o widget persiste as preferências do usuário. */
const CHAVE_PREFERENCIAS = 'asw-user-settings';

/**
 * Os elementos que a lista do widget NÃO alcança.
 *
 * Só o que falta, de propósito: o que ele já trata continua sendo problema dele, e repetir o
 * trabalho só criaria duas versões da mesma regra. `div` fica de fora porque escalar contêiner
 * fixa um `px` absoluto que quebra todo filho medido em `em` — é a razão de o próprio widget
 * não o incluir.
 */
const COMPLEMENTO = [
  'label',
  'button',
  'input',
  'select',
  'textarea',
  'option',
  'optgroup',
  'legend',
  'caption',
  'summary',
  'output',
  'small',
  'strong',
  'b',
  'em',
  'dd',
  'figcaption',
].join(',');

/** A lista do próprio widget, para alcançar o que entra no DOM depois. */
const DO_WIDGET = 'h1,h2,h3,h4,h5,h6,p,a,dl,dt,li,ol,th,td,span,blockquote,.asw-text';

const SELETOR = `${DO_WIDGET},${COMPLEMENTO}`;

/**
 * A lista FIXA de exceções do widget: elementos de ícone não são escalados, senão o glifo cresce
 * junto e estoura a caixa do botão. Copiada dele para que os dois concordem.
 */
const TOKENS_DE_ICONE = new Set([
  'material-icons', 'fa', 'fa-solid', 'fa-regular', 'fa-brands', 'glyphicon', 'icon', 'mdi',
  'ion', 'svg-icon', 'iconfont', 'emoji-icon', 'bi', 'octicon', 'feather', 'heroicon', 'bx', 'ri',
]);

function ehIcone(elemento: HTMLElement): boolean {
  for (const classe of Array.from(elemento.classList)) {
    if (TOKENS_DE_ICONE.has(classe)) return true;
  }

  return false;
}

/** O painel do próprio widget tem tipografia própria e não deve ser escalado. */
function ehDoWidget(elemento: HTMLElement): boolean {
  return elemento.closest('.asw-widget, .asw-menu, .asw-rg-container') !== null;
}

/** O fator vigente, lido de onde o widget o guarda. 1 = sem ajuste. */
function fatorAtual(): number {
  try {
    const bruto = window.localStorage.getItem(CHAVE_PREFERENCIAS);

    if (!bruto) return 1;

    const fator = Number(JSON.parse(bruto)?.states?.fontSize);

    // O widget limita entre 0,1 e 2; fora disso é dado estragado e não se aplica nada.
    return Number.isFinite(fator) && fator >= 0.1 && fator <= 2 ? fator : 1;
  } catch {
    // Janela anônima, armazenamento bloqueado ou JSON corrompido: sem ajuste.
    return 1;
  }
}

function aplicarNoElemento(elemento: HTMLElement, fator: number): void {
  if (ehIcone(elemento) || ehDoWidget(elemento)) return;

  /*
   * O tamanho ORIGINAL vem da marca que o widget grava, e é gravado por nós quando ele ainda
   * não passou por aqui. É o que torna a operação idempotente: a conta parte sempre do tamanho
   * de origem, nunca do já escalado — sem isto, cada reaplicação multiplicaria de novo.
   */
  let original = Number(elemento.dataset.aswOrgFontSize);

  if (!Number.isFinite(original) || original <= 0) {
    original = parseFloat(window.getComputedStyle(elemento).fontSize);

    if (!Number.isFinite(original) || original <= 0) return;

    elemento.dataset.aswOrgFontSize = String(original);
  }

  if (fator === 1) {
    /*
     * Voltar ao normal REMOVE o estilo em vez de gravar o tamanho original em px.
     *
     * Gravar congelaria um pixel: a folha de estilo deixaria de mandar naquele elemento, e um
     * combo que mede em `em`, ou um tema que muda a fonte base, não teria mais efeito sobre
     * ele. Removendo, o valor volta a ser o da folha, que é o estado de verdade.
     */
    elemento.style.removeProperty('font-size');

    return;
  }

  /*
   * `important` NÃO é exagero: sem ele o ajuste não aparece.
   *
   * O tema tem utilitárias com prioridade — `.font-14 { font-size: 14px !important }` e
   * companhia — e um estilo inline SEM prioridade perde para uma declaração de folha COM
   * prioridade. Era o defeito visível no "Código INEP" da Home: o widget gravava
   * `style="font-size: 5.6px"` no `<p class="font-14">` e a tela não mudava nada.
   *
   * É o mesmo empate descrito em `docs/acessibilidade` §2.1, agora do nosso lado: aqui a
   * prioridade é legítima, porque isto é preferência explícita do usuário sobre tipografia —
   * exatamente o caso em que ela deve vencer o tema.
   */
  elemento.style.setProperty('font-size', `${original * fator}px`, 'important');
}

function aplicarEm(raiz: ParentNode, fator: number): void {
  if (raiz instanceof HTMLElement && raiz.matches(SELETOR)) aplicarNoElemento(raiz, fator);

  for (const elemento of Array.from(raiz.querySelectorAll<HTMLElement>(SELETOR))) {
    aplicarNoElemento(elemento, fator);
  }
}

/** Reaplica a escala vigente no documento inteiro. */
export function reaplicarEscalaDeFonte(): void {
  aplicarEm(document, fatorAtual());
}

/**
 * Liga o complemento. Chamado uma vez, na subida da aplicação.
 *
 * <p>
 * O observador olha apenas os nós ACRESCENTADOS — não varre o documento a cada mutação. Enquanto
 * o fator é 1 (o caso de quase todo usuário) o trabalho por lote é uma leitura de
 * `localStorage` e nada mais.
 * </p>
 */
export function completarEscalaDeFonteDoWidget(): void {
  if (typeof document === 'undefined') return;

  reaplicarEscalaDeFonte();

  /*
   * O widget grava o novo fator no `localStorage` e aplica na hora, mas não avisa ninguém: o
   * evento `storage` só dispara em OUTRA aba. Ouvir o clique nos botões dele é o que sobra —
   * delegado no documento, porque o painel é montado e desmontado a cada abertura.
   *
   * O `setTimeout(0)` espera o widget gravar o estado antes de lermos.
   */
  document.addEventListener('click', (evento) => {
    const alvo = evento.target as HTMLElement | null;

    if (!alvo?.closest?.('.asw-plus, .asw-minus, .asw-menu-reset')) return;

    window.setTimeout(reaplicarEscalaDeFonte, 0);
  });

  const observador = new MutationObserver((mutacoes) => {
    const fator = fatorAtual();

    // Sem ajuste ligado não há o que fazer com os nós novos.
    if (fator === 1) return;

    for (const mutacao of mutacoes) {
      for (const no of Array.from(mutacao.addedNodes)) {
        if (no instanceof HTMLElement) aplicarEm(no, fator);
      }
    }
  });

  observador.observe(document.documentElement, { childList: true, subtree: true });
}
